import React, { useMemo, useState } from 'react';
import { Translate} from '../../config/TranslationProvider';
import { endOfToday, formatISO, startOfToday } from 'date-fns';
import { useAppConfiguration } from '../../AppConfiguration';
import { isPlaygroundsFilterSelected, playgroundsTag, usePlaygroundsFilter } from '../../util/playgrounds';
import { useEvents as useApiEvents, useLocations as useApiLocations } from '../../api/hooks';
import MapMultiple, { CachedMarkerClusterGroup } from './MapMultiple';
import LocationMarker from './LocationMarker';
import EventMarker from './EventMarker';
import LoadOtherResourcesSwitch from '../filter/fields/LoadOtherResourcesSwitch';

const useLocations = (initialLocations, filter, playgroundsFilterSelected, boundingBox) => {
    const [locations, setLocations] = useState(initialLocations);
    const {data: newLocations, isLoading: locationsLoading} = useApiLocations(
        {
            ...filter,
            bbox: boundingBox,
        },
        500,
        0,
        {enabled: !playgroundsFilterSelected && !!boundingBox},
    );

    // Don't lose old locations while updating them for the new bounding box
    if (newLocations && newLocations !== locations) {
        setLocations(newLocations);
    }

    // If we already have locations but the playgrounds filter gets selected, remove the existing locations as they
    // won't be set due to the query being disabled above when the playgrounds filter is selected
    if (locations && locations.data.length > 0 && playgroundsFilterSelected) {
        setLocations({
            data: [],
        });
    }

    return {locations, locationsLoading};
};

const usePlaygrounds = (filter, playgroundsFilterSelected, boundingBox, locationsFilterConfiguration) => {
    const [showPlaygrounds, setShowPlaygrounds] = useState(false);

    // Force showing playgrounds if the playgrounds tag is selected by a filter
    if (showPlaygrounds === false && playgroundsFilterSelected) {
        setShowPlaygrounds(true);
    }

    // If TagDropdown is enabled, remove the tags in it from the playgrounds filter.
    // These tags are usually used as type selector but playgrounds usually don't have another type than "playgrounds".
    let removeTags = [];
    if (locationsFilterConfiguration.tagDropdown?.enabled) {
        removeTags = locationsFilterConfiguration.tagDropdown.tags;
    }

    // Alter locations filter to load all playgrounds
    const playgroundsFilter = usePlaygroundsFilter(filter, removeTags);

    const [playgrounds, setPlaygrounds] = useState(null);
    const {data: newPlaygrounds, isLoading: playgroundsLoading} = useApiLocations(
        {
            ...playgroundsFilter,
            bbox: boundingBox,
        },
        500,
        0,
        {enabled: showPlaygrounds && !!boundingBox},
    );

    // Don't lose old locations while updating them for the new bounding box
    if (newPlaygrounds && newPlaygrounds !== playgrounds) {
        setPlaygrounds(newPlaygrounds);
    }

    return {showPlaygrounds, setShowPlaygrounds, playgrounds, playgroundsLoading};
};

const useEvents = (filter, boundingBox, locationsFilterConfiguration) => {
    const [showEvents, setShowEvents] = useState(false);

    const eventsFilter = {
        // Set required event date to today, since there is no date in locations filter
        'end_date_from': formatISO(startOfToday()),
        'start_date_to': formatISO(endOfToday()),
        ...filter,
    };

    // If TagDropdown is enabled, remove the tags in it from the events filter in case we have no events with those
    // tags
    if (eventsFilter.tags && locationsFilterConfiguration.tagDropdown?.enabled) {
        eventsFilter.tags = {
            operation: 'AND',
            values: filter.tags.values || filter.tags,
        };

        const tagsToRemove = locationsFilterConfiguration.tagDropdown.tags;
        eventsFilter.tags.values = eventsFilter.tags.values.filter(tag => !tagsToRemove.includes(tag));
    }

    const [events, setEvents] = useState(null);
    const {data: newEvents, isLoading: eventsLoading} = useApiEvents(
        {
            ...eventsFilter,
            bbox: boundingBox,
        },
        500,
        0,
        {enabled: showEvents && !!boundingBox},
    );

    // Don't lose old events while updating them for the new bounding box
    if (newEvents && newEvents !== events) {
        setEvents(newEvents);
    }

    return {showEvents, setShowEvents, events, eventsLoading};
};

const MultipleMapLocations = ({initialLocations, filter}) => {
    const playgroundsFilterSelected = isPlaygroundsFilterSelected(filter);

    const {mapConfiguration: {allowLoadingOtherResources}, locationsFilterConfiguration} = useAppConfiguration();
    const [boundingBox, setBoundingBox] = useState(null);
    const {locations, locationsLoading} = useLocations(
        initialLocations,
        filter,
        playgroundsFilterSelected,
        boundingBox,
    );

    // Get data for the "Show all playgrounds" and "Add events" buttons
    const {
        showPlaygrounds,
        setShowPlaygrounds,
        playgrounds,
        playgroundsLoading,
    } = usePlaygrounds(filter, playgroundsFilterSelected, boundingBox, locationsFilterConfiguration);
    const {showEvents, setShowEvents, events, eventsLoading} = useEvents(filter, boundingBox, locationsFilterConfiguration);

    // Build markers
    const items = [];
    // Don't show playgrounds by default
    const locationsWithoutPlaygrounds = locations.data.filter(({tags}) => {
        if (!tags) {
            // Allow locations without tags
            return true;
        }

        // Remove locations that have the playgrounds tag
        for (const tag of tags) {
            if (tag.title === playgroundsTag) {
                return false;
            }
        }

        return true;
    });
    for (const location of locationsWithoutPlaygrounds) {
        // Since we get results like 13.5151515,514 we have to split it to make sure we always get valid coordinates
        location.lat = location.lat.split(',')[0];
        location.lng = location.lng.split(',')[0];

        items.push({
            lat: location.lat.split(',')[0],
            lng: location.lng.split(',')[0],
            marker: <LocationMarker key={location.id} location={location} popup={true} />,
        });
    }

    if (showEvents && events) {
        for (const event of events.data) {
            // Skip events without dates. This can happen if the event was rescheduled and the built filter query somehow
            // does match the event but the actual event dates don't.
            if (!event.event_dates || event.event_dates.length <= 0) {
                continue;
            }

            // Since we get results like 13.5151515,514 we have to split it to make sure we always get valid coordinates
            event.location.lat = event.location.lat.split(',')[0];
            event.location.lng = event.location.lng.split(',')[0];
            items.push({
                // Since we get results like 13.5151515,514 we have to split it to make sure we always get valid coordinates
                lat: event.location.lat,
                lng: event.location.lng,
                marker: <EventMarker key={event.id} event={event} popup={true} />,
            });
        }
    }

    const center = useMemo(() => {
        if (!filter.radius) {
            return undefined; // Needed for javascript to use the default value
        }

        const [lat, lng] = filter.radius.split(',');

        // TODO: Use radius and calculate zoom level
        return [lat, lng];
    }, [filter]);

    return (
        <MapMultiple
            center={center}
            items={items}
            info={<Translate value="map_popup_locations" />}
            loading={locationsLoading || (showPlaygrounds && playgroundsLoading) || (showEvents && eventsLoading)}
            boundingBox={boundingBox}
            setBoundingBox={setBoundingBox}
        >
            <LoadOtherResourcesSwitch
                showOtherResources={showPlaygrounds}
                setShowOtherResources={setShowPlaygrounds}
                translationKey="show_playgrounds"
                row={1}
                disabled={playgroundsFilterSelected}
            />

            {allowLoadingOtherResources && (
                <LoadOtherResourcesSwitch
                    showOtherResources={showEvents}
                    setShowOtherResources={setShowEvents}
                    translationKey="add_events"
                    row={2}
                />
            )}

            {showPlaygrounds && playgrounds && playgrounds.data && (
                <CachedMarkerClusterGroup markers={playgrounds.data.map(playground => (
                    <LocationMarker key={playground.id} location={playground} popup={true} />
                ))} />
            )}
        </MapMultiple>
    );
};

export default MultipleMapLocations;
